<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLicenseStore } from '@/stores/license'
import { licenseTypeLabel } from '@/utils/licenseTypes'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import { usePermissions } from '@/composables/usePermissions'
import { BaseButton, BaseModalClose } from '@/components/base'

interface RestaurantLicense {
  id: string
  license_type: string
  billing_period: string
  status: string
  purchased_at: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  kiosk: { id: number; name: string } | null
}

interface RestaurantProp {
  id: string | number
  name: string
  licenses?: RestaurantLicense[]
}

const props = defineProps<{
  subscriptionId?: string
  restaurant?: RestaurantProp
}>()
const emit = defineEmits<{ close: []; cancelled: []; error: [message: string] }>()

const { t, locale } = useI18n()
const licenseStore = useLicenseStore()
const { can } = usePermissions()

const isSubscriptionMode = computed(() => !!props.subscriptionId)

const subscriptionLicenses = computed(() =>
  props.subscriptionId ? (licenseStore.licenses[props.subscriptionId] || []) : []
)
const restaurantLicenses = computed<RestaurantLicense[]>(() =>
  props.restaurant?.licenses || []
)

const licenses = computed(() =>
  isSubscriptionMode.value ? subscriptionLicenses.value : restaurantLicenses.value
)

const isLoading = computed(() => isSubscriptionMode.value && licenseStore.licensesLoading)
const loadError = computed(() => isSubscriptionMode.value ? licenseStore.licensesError : null)

const cancelingId = ref<string | null>(null)

onMounted(() => {
  if (props.subscriptionId) {
    licenseStore.fetchLicenses(props.subscriptionId)
  }
})

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—'
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(new Date(dateStr))
}

const statusClass = (status: string) => {
  switch (status) {
    case 'active':    return 'bg-green-50 text-green-700'
    case 'pending':   return 'bg-amber-50 text-amber-700'
    case 'suspended': return 'bg-orange-50 text-orange-700'
    default:          return 'bg-red-50 text-red-700'
  }
}

const modalSubtitle = computed(() =>
  isSubscriptionMode.value ? t('licenses.licensesList.subtitle') : (props.restaurant?.name ?? '')
)

const getLicenseRestaurantName = (license: any): string =>
  license.restaurant?.name || '—'

const canCancel = (license: any) =>
  license.status === 'active' && !license.cancel_at_period_end

const handleConfirmCancel = async () => {
  if (!cancelingId.value) return
  const id = cancelingId.value
  cancelingId.value = null
  const ok = await licenseStore.cancelLicense(id)
  if (ok) {
    await licenseStore.fetchLicenses(props.subscriptionId!)
    emit('cancelled')
  } else {
    emit('error', licenseStore.cancelLicenseError || t('licenses.licensesList.cancelError'))
  }
}
</script>

<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">

      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 class="text-xl font-semibold text-gray-900">{{ t('licenses.licensesList.title') }}</h2>
          <p class="text-sm text-gray-500 mt-0.5">
            {{ modalSubtitle }}
          </p>
        </div>
        <BaseModalClose @click="emit('close')" />
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-auto p-6">

        <!-- Loading (subscription mode only) -->
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <svg class="animate-spin w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        </div>

        <!-- Error fetching -->
        <div v-else-if="loadError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-700 text-sm font-medium">{{ loadError }}</p>
        </div>

        <!-- Empty -->
        <div v-else-if="licenses.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
          <svg class="w-10 h-10 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p class="text-sm text-gray-500">{{ t('licenses.licensesList.empty') }}</p>
        </div>

        <!-- Table -->
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr class="bg-gray-50">
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-8">#</th>
                <!-- Restaurant column: subscription mode only -->
                <th v-if="isSubscriptionMode" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ t('licenses.licensesList.restaurant') }}</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ t('licenses.licensesList.mode') }}</th>
                <!-- Billing column: restaurant mode only -->
                <th v-if="!isSubscriptionMode" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ t('licenses.licensesList.billing') }}</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ t('licenses.licensesList.status') }}</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ t('licenses.licensesList.purchasedAt') }}</th>
                <!-- Actions column: subscription mode only -->
                <th v-if="isSubscriptionMode" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ t('licenses.licensesList.actions') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 bg-white">
              <tr v-for="(license, index) in licenses" :key="license.id" class="hover:bg-gray-50 transition-colors">
                <td class="px-4 py-3 text-gray-400 text-xs">{{ index + 1 }}</td>
                <!-- Restaurant name: subscription mode -->
                <td v-if="isSubscriptionMode" class="px-4 py-3 font-medium text-gray-900">{{ getLicenseRestaurantName(license) }}</td>
                <td class="px-4 py-3 text-gray-700">
                  {{ licenseTypeLabel(license.license_type) }}
                  <span v-if="license.kiosk" class="text-gray-400 text-xs">({{ license.kiosk.name }})</span>
                </td>
                <!-- Billing period: restaurant mode -->
                <td v-if="!isSubscriptionMode" class="px-4 py-3 text-gray-600 capitalize">{{ license.billing_period }}</td>
                <td class="px-4 py-3">
                  <span class="px-2 py-0.5 text-xs font-medium rounded capitalize" :class="statusClass(license.status)">
                    {{ t(`licenses.licensesList.statusLabels.${license.status}`, license.status) }}
                  </span>
                  <p v-if="license.cancel_at_period_end && license.status === 'active'" class="text-amber-600 text-xs mt-0.5">
                    {{ t('licenses.licensesList.cancelsAt', { date: formatDate(license.current_period_end) }) }}
                  </p>
                </td>
                <td class="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{{ formatDate(license.purchased_at) }}</td>
                <!-- Cancel action: subscription mode only -->
                <td v-if="isSubscriptionMode" class="px-4 py-3">
                  <button
                    v-if="canCancel(license) && can('licenses:delete')"
                    type="button"
                    @click="cancelingId = license.id"
                    class="text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
                  >
                    {{ t('licenses.licensesList.cancel') }}
                  </button>
                  <span v-else class="text-xs text-gray-300">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
        <span class="text-sm text-gray-500">{{ t('licenses.licensesList.totalCount', licenses.length) }}</span>
        <BaseButton
          @click="emit('close')"
          variant="secondary"
        >
          {{ t('licenses.licensesList.close') }}
        </BaseButton>
      </div>

    </div>
  </div>

  <!-- Cancel confirmation popup (subscription mode only) -->
  <ConfirmationModal
    v-if="isSubscriptionMode"
    :is-open="cancelingId !== null"
    :title="t('licenses.licensesList.cancelConfirmTitle')"
    :description="t('licenses.licensesList.cancelConfirmDescription')"
    :confirm-text="t('licenses.licensesList.cancelConfirmButton')"
    :is-loading="licenseStore.cancelLicenseLoading !== null"
    variant="danger"
    @confirm="handleConfirmCancel"
    @cancel="cancelingId = null"
  />

</template>
